import { useEffect, useMemo, useState } from "react";
import { formatMoney } from "../../../features/utils/formatMoney";
import { clone } from "../../../features/utils/objClone";
import PassengerBreakdownCard from "./PassengerBreakdownCard";

export const PriceBreakdown = ({ data: defData, callback }) => {
  const [data, setData] = useState({
    refundAmount: 0,
    fee: 0,
    passengerPrice: [],
  });

  const [lock, setLock] = useState(true);

  const travellerMap = useMemo(
    () => ({
      ADT: "ADT",
      Adult: "ADT",
      CHD: "CHD",
      CNN: "CHD",
      Child: "CHD",
      INS: "INF",
      INF: "INF",
      Infant: "INF",
    }),
    [],
  );

  const travellerLabel = {
    ADT: "Adult",
    CHD: "Child",
    INF: "Infant",
  };

  const baseBreakdown = useMemo(
    () => [
      { name: "Refund Fee", amount: 0, isDeducted: true },
      { name: "Non-refundable Fees (All Taxes)", amount: 0, isDeducted: true },
      // { name: "Original airline fees", amount: 0, isDeducted: false },
    ],
    [],
  );

  const passengerIds = useMemo(() => defData?.passengerIds || [], [defData]);

  // ✅ these are ONLY the travellers who requested refund
  const selectedTravellers = useMemo(() => {
    const all = defData?.flightObj?.travelers || [];
    return all.filter((t) => passengerIds.includes(t?.id));
  }, [defData, passengerIds]);

  // ✅ count requested-refund travellers by normalized type
  const travellersByTypeCount = useMemo(() => {
    const map = {};
    for (const t of selectedTravellers) {
      const normalized =
        travellerMap[t?.travelerType] || t?.travelerType || "ADT";
      map[normalized] = (map[normalized] || 0) + 1;
    }
    return map;
  }, [selectedTravellers, travellerMap]);

  const travelersInit = useMemo(() => {
    // Prefer saved passengerPrice if provided
    if (defData?.isQuoted && defData?.passengerPrice?.length) {
      return defData.passengerPrice.map((p) => ({
        travelerType: p?.travelerType,
        breakDown: p?.breakDown?.length ? p.breakDown : clone(baseBreakdown),
      }));
    }

    // Else build from selected travellers (can include duplicates by type; we dedupe later for totals/UI)
    return selectedTravellers.map((t) => ({
      travelerType: t?.travelerType,
      breakDown: clone(baseBreakdown),
    }));
  }, [defData, selectedTravellers, baseBreakdown]);

  useEffect(() => {
    setData({
      fee: defData?.fee || 0,
      refundAmount: defData?.payable || 0,
      passengerPrice: travelersInit,
    });
    // eslint-disable-next-line
  }, [defData]);

  useEffect(() => {
    if (lock) setLock(false);
    else callback && callback(data);
  }, [data]); // eslint-disable-line

  // ✅ unique by travelerType for UI + correct totals
  const uniquePassengerPrice = useMemo(() => {
    const arr = data?.passengerPrice || [];
    return arr.filter(
      (item, i) =>
        arr.findIndex((x) => x?.travelerType === item?.travelerType) === i,
    );
  }, [data?.passengerPrice]);

  const getBreakdownTotal = (item) => {
    return (item?.breakDown || []).reduce((acc, cur) => {
      if (!cur?.isDeducted) return acc;
      return acc + Number(cur?.amount || 0);
    }, 0);
  };

  const getIntraverseFeeMeta = (travelerType) => {
    const normalized = travellerMap[travelerType] || travelerType || "ADT";

    const intraverseTravelerFee = defData?.intraverseFeeBreakdown?.find(
      (fee) => travellerMap[fee?.passengerType] === normalized,
    );

    const fallbackServiceFee =
      Number(defData?.intraverseFee || 0) / Math.max(passengerIds.length, 1);

    const passengerCount = Math.max(
      intraverseTravelerFee?.passengerCount ||
        travellersByTypeCount[normalized] ||
        1,
      1,
    );

    const serviceFee =
      intraverseTravelerFee?.amount != null
        ? Number(intraverseTravelerFee.amount) || 0
        : fallbackServiceFee;

    return {
      normalized,
      intraverseTravelerFee,
      // serviceFee: serviceFee / passengerCount,
      serviceFee: serviceFee,
      passengerCount,
    };
  };

  const travelersTotal = useMemo(() => {
    return uniquePassengerPrice.reduce((acc, item) => {
      const { serviceFee, passengerCount } = getIntraverseFeeMeta(
        item?.travelerType,
      );
      const bd = getBreakdownTotal(item) || 0;

      return acc + bd * passengerCount + serviceFee * passengerCount;
    }, 0);
  }, [uniquePassengerPrice, defData, passengerIds, travellersByTypeCount]);

  const passengerTotal = useMemo(() => {
    const travellers = defData?.flightObj?.travelers || [];

    const selected = passengerIds
      .map((id) => travellers.find((t) => t?.id === id))
      .filter(Boolean);

    const parsePrice = (price) => {
      if (typeof price !== "string") return Number(price || 0);
      const numeric = parseFloat(
        price.replace(/^[A-Za-z\s]+/, "").replaceAll(",", ""),
      );
      return Number.isFinite(numeric) ? numeric : 0;
    };

    const travelerDict = {
      ADT: defData?.flightObj?.adult || {},
      CHD: defData?.flightObj?.child || {},
      CNN: defData?.flightObj?.child || {},
      INF: defData?.flightObj?.infant || {},
      INS: defData?.flightObj?.infant || {},
    };

    return selected.reduce((acc, p) => {
      const pricing = travelerDict[p?.travelerType || "ADT"] || {};
      // return acc + parsePrice(pricing?.totalPrice);
      // console.log({ pricing, defData });
      return (
        acc + parsePrice(pricing?.totalPrice) / (pricing?.passengerCount || 1)
      );
    }, 0);
  }, [defData, passengerIds]);

  const handleBreakdown = (i, val, travelerType) => {
    const next = clone(data?.passengerPrice) || [];

    next.forEach((p) => {
      if (p?.travelerType === travelerType && p.breakDown?.[i]) {
        p.breakDown[i].amount = Number(val || 0);
      }
    });

    setData((prev) => ({
      ...prev,
      passengerPrice: next,
      refundAmount: (defData?.flightObj?.payable || 0) - travelersTotal,
    }));
  };

  // ✅ subtotal shown inside the card: (breakdown + service) * passengerCount (so it matches contribution)
  const getSubtotalForItem = (item) => {
    const { serviceFee, passengerCount } = getIntraverseFeeMeta(
      item?.travelerType,
    );

    const bd = getBreakdownTotal(item) || 0;
    return (bd + serviceFee) * passengerCount;
  };

  const handleSaveBreakdown = (travelerType, nextBreakDown) => {
    const next = clone(data?.passengerPrice) || [];

    next.forEach((p) => {
      if (p?.travelerType === travelerType) {
        p.breakDown = clone(nextBreakDown || []);
      }
    });

    setData((prev) => ({
      ...prev,
      passengerPrice: next,
    }));
  };

  return (
    <div className="w-full bg-[#F6F7FB] flex flex-col p-4 mt-4 rounded-2xl border border-gray-200">
      {defData?.supplierRefundAmount ? (
        <div className="flex items-center justify-between rounded-xl bg-white border border-gray-200 px-4 py-3">
          <div className="text-sm text-gray-600">
            Original Supplier Refund Amount
          </div>
          <div className=" text-red-900">
            {formatMoney(defData?.supplierRefundAmount)}
          </div>
        </div>
      ) : (
        <></>
      )}
      <div className="mt-4 space-y-4 pb-4">
        {uniquePassengerPrice.map((item, index) => {
          const { normalized, serviceFee, passengerCount } =
            getIntraverseFeeMeta(item?.travelerType);

          return (
            <PassengerBreakdownCard
              key={index}
              normalized={normalized}
              serviceFee={serviceFee}
              passengerCount={passengerCount}
              item={item}
              travelerType={item?.travelerType}
              travellerLabel={travellerLabel}
              handleSaveBreakdown={handleSaveBreakdown}
              getSubtotalForItem={getSubtotalForItem}
              defData={defData}
            />
          );
        })}
      </div>
      {/* <div className="flex items-center justify-between rounded-xl bg-white border border-gray-200 px-4 py-3">
        <div className="text-sm text-gray-600">Total Refund Fee</div>
        <div className="font-bold text-gray-900">
          {formatMoney(travelersTotal)}
        </div>
      </div> */}
      {/* <div className="w-full border-b border-gray-200 my-4" />

      <div className="flex items-center justify-between">
        <div className="text-base font-semibold text-gray-800">
          Final Refund Value
        </div>
        <div className="text-lg font-bold text-primary1">
          {formatMoney((passengerTotal || 0) - travelersTotal)}
        </div>
      </div> */}
    </div>
  );
};
